import { Form, Formik, getIn } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl,
  FormHelperText, Grid, InputLabel, Select, TextField, Typography
} from '../../utils/material-ui-core';

interface ConfirmProps {
  title: string;
  content: string;
  yesText: string;
  noText: string;
  open: boolean;
  reasonIdLabel: string;
  reasonNoteLabel: string;
  reasonLookups: Lookup[];
  callbackOnConfirm: (confirmWithSelectReasonOutput: ConfirmWithSelectReasonOutput) => void;
}

const formDefaultValue = {
  id: 0,
  val: "",
};

const formDefaultTouchValue = {
  id: false,
  val: false,
};

let formValidationSchema = Yup.object().shape({});

const ConfirmWithSelectReason = ({ title, content, yesText, noText, open, reasonIdLabel, reasonNoteLabel, reasonLookups, callbackOnConfirm }: ConfirmProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;
  const [initialFormValues, setInitialFormValues] = useState<{ id: number; val: any }>(formDefaultValue);

  const confirm = () => {
    const confirmWithSelectReasonOutput: ConfirmWithSelectReasonOutput = {
      confirm: true,
      reasonId: formReference.current.values.id,
      note: formReference.current.values.val,
    };
    callbackOnConfirm(confirmWithSelectReasonOutput);
  };

  const cancel = () => {
    const confirmWithSelectReasonOutput: ConfirmWithSelectReasonOutput = {
      confirm: false,
      reasonId: 0,
      note: "",
    };
    callbackOnConfirm(confirmWithSelectReasonOutput);
  };

  useEffect(() => {
    if (open) {
      formValidationSchema = Yup.object().shape({
        id: Yup.number()
          .required(reasonIdLabel + " is required.")
          .positive(reasonIdLabel + " is required."),
      });
      setInitialFormValues({ id: 0, val: "" });
    }
  }, [open, reasonIdLabel, reasonNoteLabel, title]);

  return (
    <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
      <DialogTitle className={globalStyles.dialogTitleContainer}>
        <Typography variant="body1" component="span">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText className={pageStyles.contentText} component="div">
          <HtmlContent dangerouslySetInnerHTML={{ __html: content }}></HtmlContent>

          <Formik
            innerRef={formReference}
            initialValues={initialFormValues}
            validationSchema={formValidationSchema}
            validateOnMount={true}
            initialTouched={formDefaultTouchValue}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values: any) => {
              confirm();
            }}
          >
            {({ values, errors, handleChange, setFieldValue, touched, handleBlur, isSubmitting, validateForm }) => (
              <Form>
                <Grid container spacing={3} className={globalStyles.formikContainer}>
                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "id"))}>
                      <InputLabel>{reasonIdLabel}</InputLabel>
                      <Select native labelId="id" id="id" name="id" value={values.id} onChange={handleChange} onBlur={handleBlur} label="Reason">
                        <option value={-1}></option>
                        {reasonLookups.map((reasonLookup, index) => (
                          <option value={reasonLookup.value} key={index}>
                            {reasonLookup.name}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText error>{getIn(errors, "id")}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <TextField autoFocus multiline={true} margin="dense" id="val" name="val" value={values.val} label={reasonNoteLabel} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(touched, "val") && getIn(errors, "val"))} helperText={getIn(touched, "val") && getIn(errors, "val")} />
                    </FormControl>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" autoFocus>
          {yesText}
        </Button>
        <Button onClick={() => cancel()} className={globalStyles.buttonDefault} autoFocus>
          <u>{noText}</u>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
    },
    deleteButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  })
);

export default ConfirmWithSelectReason;
